import { useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../api";
import type { UsageReport } from "../types";
import { dayBuckets, fmtUsd, providerColor, type DayBucket } from "./usageChart";
import { resetText, windowLabel } from "./quotaText";
import { MenuIcon } from "../icons";

const RANGES = [7, 30, 90] as const;

/** Where the two providers without a balance API show their own numbers. */
const DASHBOARDS: Record<string, string> = {
	openai: "https://platform.openai.com/usage",
	google: "https://aistudio.google.com/usage",
};

/** What each kind of spend is called; an unknown kind shows as recorded. */
const KIND_LABELS: Record<string, string> = {
	message: "对话",
	compaction: "上下文压缩",
	cache_warm: "缓存保温",
	title: "标题",
	memory: "记忆整理",
	slides: "幻灯片",
	worker: "编码执行器",
};

const fmtTokens = (n: number) => (n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(0)}k` : String(n));
const workerCost=(row:{cost:number;unknownCosts?:number;messages:number;estimatedCosts?:number})=>row.unknownCosts ? row.unknownCosts===row.messages ? "未定价" : `${fmtUsd(row.cost)}＋未定价` : row.estimatedCosts ? `${fmtUsd(row.cost)}（估算）` : fmtUsd(row.cost);

/**
 * Spend per day, stacked by provider. Plain SVG: a handful of bars needs no library. Bars keep
 * a two-pixel gap between stacked segments and between days; a hovered day names its parts.
 */
function SpendChart({ buckets, providers }: { buckets: DayBucket[]; providers: string[] }) {
	const [hover, setHover] = useState<number | null>(null);
	const W = 720;
	const H = 180;
	const pad = { top: 8, right: 8, bottom: 22, left: 44 };
	const max = Math.max(...buckets.map((b) => b.total), 0.01);
	const innerW = W - pad.left - pad.right;
	const innerH = H - pad.top - pad.bottom;
	const slot = innerW / buckets.length;
	const barW = Math.max(2, slot - 2);
	const y = (v: number) => pad.top + innerH - (v / max) * innerH;
	const ticks = [0, max / 2, max];
	// Enough day labels to read the axis, never so many they collide.
	const every = Math.ceil(buckets.length / 6);
	const hovered = hover === null ? null : buckets[hover]!;

	return (
		<div className="spend-chart">
			<svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="每日花费" onMouseLeave={() => setHover(null)}>
				{ticks.map((t) => (
					<g key={t}>
						<line x1={pad.left} x2={W - pad.right} y1={y(t)} y2={y(t)} className="grid" />
						<text x={pad.left - 6} y={y(t) + 4} textAnchor="end" className="axis">
							{t >= 1 ? `$${t.toFixed(0)}` : `$${t.toFixed(2)}`}
						</text>
					</g>
				))}
				{buckets.map((b, i) => {
					const x = pad.left + i * slot + 1;
					let top = y(0);
					const segments = providers
						.filter((p) => (b.parts[p] ?? 0) > 0)
						.map((p) => {
							const h = ((b.parts[p] ?? 0) / max) * innerH;
							top -= h;
							return { p, yTop: top, h };
						});
					return (
						<g key={b.day} onMouseEnter={() => setHover(i)}>
							<rect x={x - 1} y={pad.top} width={slot} height={innerH} className="hit" />
							{segments.map((s, j) => (
								<rect key={s.p} x={x} y={s.yTop + (j < segments.length - 1 ? 2 : 0)} width={barW} height={Math.max(0, s.h - (j < segments.length - 1 ? 2 : 0))} rx={j === segments.length - 1 ? 3 : 0} fill={providerColor(s.p)} />
							))}
							{i % every === 0 && (
								<text x={x + barW / 2} y={H - 6} textAnchor="middle" className="axis">
									{b.day.slice(5).replace("-", "/")}
								</text>
							)}
						</g>
					);
				})}
			</svg>
			<div className="spend-legend">
				{providers.map((p) => (
					<span key={p}>
						<i style={{ background: providerColor(p) }} /> {p}
					</span>
				))}
				{hovered && (
					<span className="spend-tip">
						{hovered.day} · {hovered.unknownCosts ? hovered.total > 0 ? `${fmtUsd(hovered.total)}＋未定价` : "未定价" : fmtUsd(hovered.total)}
						{providers
							.filter((p) => (hovered.parts[p] ?? 0) > 0)
							.map((p) => ` · ${p} ${fmtUsd(hovered.parts[p]!)}`)
							.join("")}
					</span>
				)}
			</div>
		</div>
	);
}

export function Usage({ onMenu }: { onMenu: () => void }) {
	const [days, setDays] = useState<(typeof RANGES)[number]>(30);
	const [report, setReport] = useState<UsageReport | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		let gone = false;
		setReport(null);
		api
			.usage(days)
			.then((r) => {
				if (!gone) setReport(r);
			})
			.catch((err) => {
				if (!gone) setError(err instanceof ApiError ? err.message : "读取花费失败");
			});
		return () => {
			gone = true;
		};
	}, [days]);

	const buckets = useMemo(() => (report ? dayBuckets(report.byDay, report.days) : []), [report]);
	const providers = report?.byProvider.map((p) => p.provider) ?? [];
	const total = report?.byProvider.reduce((n, p) => n + p.cost, 0) ?? 0;

	return (
		<main className="chat usage">
			<header className="chat-head page-head">
				<button className="ghost sidebar-toggle icon-btn" aria-label="菜单" onClick={onMenu}>
					<MenuIcon size={22} />
				</button>
				<div className="titles">
					<span className="title">费用</span>
				</div>
				<div className="range">
					{RANGES.map((r) => (
						<button key={r} className={`ghost${r === days ? " current" : ""}`} onClick={() => setDays(r)}>
							{r} 天
						</button>
					))}
				</div>
			</header>
			{error && <p className="banner error">{error}</p>}
			<div className="usage-body">
				{!report && !error && <p className="hint">统计中…</p>}
				{report && (
					<>
						<div className="stat-row">
							<div className="stat">
								<span className="stat-label">近 {report.days} 天花费</span>
								<span className="stat-value">{workerCost({cost:total,messages:report.byProvider.reduce((n,p)=>n+p.messages,0),unknownCosts:report.byProvider.reduce((n,p)=>n+(p.unknownCosts ?? 0),0),estimatedCosts:report.byProvider.reduce((n,p)=>n+(p.estimatedCosts ?? 0),0)})}</span>
							</div>
							{report.balances.map((b) => (
								<div key={b.provider} className="stat">
									<span className="stat-label">{b.provider} 余额</span>
									<span className="stat-value">{"error" in b ? `查询失败（${b.error}）` : `${b.amount.toFixed(2)} ${b.currency}`}</span>
								</div>
							))}
							{report.quotas.map((q) =>
								"error" in q ? (
									<div key={q.provider} className="stat">
										<span className="stat-label">Codex 额度</span>
										<span className="stat-value">查询失败（{q.error}）</span>
									</div>
								) : (
									q.windows.map((w) => (
										<div key={`${q.provider}-${w.windowSeconds}`} className={`stat${w.usedPercent >= 100 ? " exhausted" : ""}`}>
											<span className="stat-label">Codex {windowLabel(w.windowSeconds)}额度</span>
											<span className="stat-value">已用 {w.usedPercent}%</span>
											<span className="stat-note">{resetText(w.resetAt)}</span>
										</div>
									))
								),
							)}
						</div>

						{providers.length > 0 ? <SpendChart buckets={buckets} providers={providers} /> : <p className="hint">暂无花费</p>}

						<div className="table-wrap">
							<h2 className="usage-group-title">LLM 费用</h2>
							<table className="data-table">
								<thead>
									<tr>
										<th>provider</th>
										<th>花费</th>
										<th>消息</th>
										<th>输入</th>
										<th>输出</th>
										<th>缓存读</th>
										<th>余额 / 账单</th>
									</tr>
								</thead>
								<tbody>
									{report.byProvider.map((p) => (
										<tr key={p.provider}>
											<td className="cell-primary">
												<i className="swatch" style={{ background: providerColor(p.provider) }} /> {p.provider}
											</td>
											<td data-label="花费">{workerCost(p)}</td>
											<td data-label="消息">{p.messages}</td>
											<td data-label="输入">{fmtTokens(p.input)}</td>
											<td data-label="输出">{fmtTokens(p.output)}</td>
											<td data-label="缓存读">{fmtTokens(p.cacheRead)}</td>
											<td data-label="余额 / 账单">
												{DASHBOARDS[p.provider] ? (
													<a href={DASHBOARDS[p.provider]} target="_blank" rel="noreferrer">
														查看账单 ↗
													</a>
												) : (
													"见上方"
												)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>

						<div className="table-wrap">
							<h2 className="usage-group-title">Profile 分布</h2>
							<table className="data-table">
								<thead>
									<tr>
										<th>profile</th>
										<th>花费</th>
										<th>消息</th>
										<th>输入</th>
										<th>输出</th>
									</tr>
								</thead>
								<tbody>
									{report.byProfile.map((p) => (
										<tr key={p.profile}>
											<td className="cell-primary">{p.profile}</td>
											<td data-label="花费">{workerCost(p)}</td>
											<td data-label="消息">{p.messages}</td>
											<td data-label="输入">{fmtTokens(p.input)}</td>
											<td data-label="输出">{fmtTokens(p.output)}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>

						<div className="table-wrap">
							<h2 className="usage-group-title">类型分布</h2>
							<table className="data-table">
								<thead>
									<tr>
										<th>类型</th>
										<th>花费</th>
										<th>调用</th>
										<th>输入</th>
										<th>输出</th>
										<th>缓存读</th>
									</tr>
								</thead>
								<tbody>
									{report.byKind.map((k) => (
										<tr key={k.kind}>
											<td className="cell-primary">{KIND_LABELS[k.kind] ?? k.kind}</td>
											<td data-label="花费">{workerCost(k)}</td>
											<td data-label="调用">{k.messages}</td>
											<td data-label="输入">{fmtTokens(k.input)}</td>
											<td data-label="输出">{fmtTokens(k.output)}</td>
											<td data-label="缓存读">{fmtTokens(k.cacheRead)}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</>
				)}
			</div>
		</main>
	);
}
